// Client-side photo shrinking + grid thumbnails (keeps the browser light with many job photos)
const MAX_EDGE = 1800;
const QUALITY = 0.8;

const loadBitmap = async (file) => {
  if (window.createImageBitmap) {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { /* fall back to <img> */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
};

// Downscale to MAX_EDGE on the long edge and re-encode as JPEG 0.8; returns the original file if it can't be decoded (e.g. HEIC)
export const downscaleImage = async (file, maxEdge = MAX_EDGE, quality = QUALITY) => {
  if (!file?.type?.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") return file;
  let bmp;
  try { bmp = await loadBitmap(file); } catch { return file; }
  const w = bmp.width, h = bmp.height;
  if (!w || !h) return file;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close?.();
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  canvas.width = canvas.height = 0;
  if (!blob || (scale === 1 && blob.size >= file.size && file.type === "image/jpeg")) return file;
  const base = (file.name || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
};

// 400px server thumbnail for a stored photo URL; other URLs (offline/blob/data) are returned unchanged
export const thumbUrl = (url) => (typeof url === "string" && url.includes("/api/files/") ? url.replace("/api/files/", "/api/files-thumb/") : url);
